import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ScrollableContainer } from "@msuite/ui/dnd";
import { useDisclosure, useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { HolidayTag } from "@msuite/ui/tag";
import { cn, responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { Holidays } from "@msuite/utils/holidays";
import { Crew } from "@/components/plan";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { COLUMN_WIDTH } from "../constants";
import { DailyContext } from "./context";
import { DailyGuestPlan } from "./daily-guest-plan";
import { DailyHeader } from "./daily-header";
import { DailyVehicles } from "./daily-vehicles";
import { useDaily } from "./use-daily";

/** Props */
interface DailyProps {
	dayIndex: number;
	date: string;
}

export function Daily({ dayIndex, date }: DailyProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure({ defaultIsOpen: moment().isSame(date, "date") });
	const vehicleDisclosure = useDisclosure();
	const showTicketsDisclosure = useDisclosure({ defaultIsOpen: true });
	const { organization } = useAuth();
	const copyLoader = useLoading();

	/** Zero */
	const {
		plan,
		ownCrews,
		partnerCrews,
		guestPlans,
		isInteractionBlocked,
		canArrangeBoard,
		canArrangeTicketOf,
		canOpenDay,
		rulesState,
	} = useDaily(date);
	const hasCrews = !!plan?.crews.length || !!guestPlans?.some((guest) => guest.crews.length);
	// The column's own date rather than the plan's, because a day nobody has opened yet has no
	// plan row and the collapsed column still shows which day it is.
	const holiday = Holidays.getHolidayByDate(moment(date).toDate(), "BE");

	// Opening a day writes the `plans` row for it, so the endpoint asks `create Plan` and this
	// request is not sent without it. A caller who may only read the board sees the same empty
	// column either way — the day simply stays unopened until somebody who plans looks at it.
	useQuery({
		queryKey: ["plan-init", date],
		queryFn: async () => {
			const response = await client.plan.initialize[":date"].$get({
				param: { date },
			});
			const data = await responseHandler(response);
			return data.success;
		},
		enabled: !!organization && !!date && canOpenDay,
	});

	if (!disclosure.isOpen) {
		return (
			<div className="w-12.5 v-stack items-center snap-start">
				<div className="v-stack items-center gap-0">
					<Button
						icon={<Icon name="ChevronRight" />}
						variant="ghost"
						onClick={disclosure.onToggle}
					/>
					<div className="h-4" />
					<p className="text-xs">{moment(date).format("dd")}</p>
					<div className="h-4" />
					<p className="text-xs">{moment(date).format("DD")}</p>
					<p className="text-xs">{moment(date).format("MMM")}</p>
					<div className="h-4" />
					<HolidayTag
						holiday={holiday}
						size="tiny"
					/>
				</div>
			</div>
		);
	}

	/** Render */
	return (
		<DailyContext.Provider
			value={{
				dayIndex,
				disclosure,
				date,
				isInteractionBlocked,
				canArrangeBoard,
				canArrangeTicketOf,
				rulesState,
				planId: plan?.id || "",
				plan,
				showTicketsDisclosure,
				vehicleDisclosure,
			}}
		>
			<div
				className={cn(
					"snap-start",
					"px-4",
					"v-stack overflow-hidden shrink-0 gap-0 max-w-full min-w-100",
				)}
				style={{
					width: COLUMN_WIDTH,
				}}
			>
				<DailyHeader
					plan={plan}
					disclosure={disclosure}
					hasCrews={hasCrews}
					copyLoader={copyLoader}
				/>
				<DailyVehicles />
				{copyLoader.isLoading ? (
					<BlankSlate
						isLoading
						withAccessory={false}
						label={t("planning.copying_day")}
					/>
				) : (
					<ScrollableContainer
						className={cn(
							"h-full overflow-y-auto scrollbar-hidden v-stack",
							hasCrews && "pb-[50%]",
						)}
					>
						{ownCrews.map((crew) => (
							<Crew
								key={crew.id}
								crewId={crew.id}
							/>
						))}
						{partnerCrews.map((crew) => (
							<Crew
								key={crew.id}
								crewId={crew.id}
							/>
						))}
						{guestPlans?.map((guestPlan) => (
							<DailyGuestPlan
								key={guestPlan.id}
								dayIndex={dayIndex}
								date={date}
								plan={guestPlan}
							/>
						))}
						{!hasCrews && (
							<BlankSlate
								withAccessory={false}
								Icon={() => (
									<Icon
										name="CalendarPlus"
										size={30}
										className={cn("stroke-1 stroke-accent-500 fill-accent-500/5")}
									/>
								)}
								label={t("planning.empty_day_label")}
							/>
						)}
					</ScrollableContainer>
				)}
			</div>
		</DailyContext.Provider>
	);
}
